import { visit } from "unist-util-visit";

interface Options {
  customClassName: string;
}

/**
 * Adds a color indicator to color codes like #CCC.
 *
 * Adapted from https://github.com/shreshthmohan/rehype-color-chips
 *
 * ISC License (ISC)
 * Copyright (c) 2022 Shreshth Mohan
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY
 * SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER
 * RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF
 * CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN
 * CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */
function plugin(options: Options = { customClassName: "gfm-color-chip" }) {
  return (tree: /* hast.Root */ any) => {
    // Build regex patterns
    const hex = "#(?:[A-Fa-f0-9]{3}){1,2}\\b";
    const num =
      "(?:(?:[1-9]?[0-9]|1[0-9][0-9]|2[0-4][0-9])(?:\\.[0-9]+)?|25[0-5](?:\\.0)?)"; // 0-255
    const alpha =
      "(?:0|1|0?\\.[0-9]+|1\\.0+|[1-9]?[0-9](?:\\.[0-9]+)?%|100(?:\\.0)?%)"; // 0-1 or 0-100%

    // rgb(R, G, B) or rgba(R, G, B, A)
    const rgbaComma = `rgba?\\(\\s*${num}\\s*(?:,\\s*${num}\\s*){2}\\s*(?:,\\s*${alpha}\\s*)?\\)`;

    // Modern rgb(R G B) or rgb(R G B / A) syntax
    const rgbModern = `rgb\\(\\s*${num}\\s+${num}\\s+${num}(?:\\s*\\/\\s*${alpha})?\\s*\\)`;

    // Combine all patterns, case insensitive, must start at beginning or after whitespace/parenthesis
    const colorRegex = new RegExp(
      `(?:^|(?<=\\s|\\(|'|"))(${hex}|${rgbaComma}|${rgbModern})`,
      "gi",
    );

    visit(tree, "text", function (node, index, parent) {
      if (!parent || index === null) return;

      // Skip if this node was created by us
      if (node.data?.processedByColorChip) return;

      // Skip if this is a URL
      if (parent.tagName === "a" && parent.properties?.href === node.value)
        return;

      const text = "value" in node && node.type === "text" ? node.value : "";

      // Quick check if there's any potential color codes before doing more work
      if (!/#|rgb/i.test(text)) return;

      let match;
      let lastIndex = 0;
      const newChildren = [];

      // Split text node and insert color chips
      while ((match = colorRegex.exec(text)) !== null) {
        const colorCode = match[0];

        // Add text before color code
        if (match.index > lastIndex) {
          newChildren.push({
            type: "text",
            value: text.slice(lastIndex, match.index),
            data: { processedByColorChip: true },
          });
        }

        // Add color chip span
        newChildren.push({
          type: "element",
          tagName: "span",
          properties: {
            className: [`${options.customClassName}-wrapper`],
            style: `display: inline-block;`,
          },
          children: [
            // Color chip
            {
              type: "element",
              tagName: "span",
              properties: {
                className: [options.customClassName],
                style: `
                  display: inline-block;
                  position: relative;
                  width: 0.75em;
                  height: 0.75em;
                  margin-right: 0.25em;
                  border: 1px solid rgb(218 217 214 / 1);
                  border-radius: 0.25em;
                  line-height: 1.5em;
                  background-color: ${colorCode};
                `,
              },
              children: [
                {
                  type: "element",
                  tagName: "span",
                  properties: {
                    className: [
                      `${options.customClassName}-tooltip`,
                      "opacity-0",
                      "transition-opacity",
                    ],
                    style: `
                      position: absolute;
                      width: 3em;
                      height: 3em;
                      left: calc(-1.125em - 1px);
                      bottom: 0.75em;
                      box-sizing: border-box;
                      border: 1px solid rgb(218 217 214 / 1);
                      border-radius: 0.25em;
                      background-color: ${colorCode};
                      color: ${colorCode};
                      pointer-events: none;
                    `,
                  },
                  children: [],
                },
              ],
            },
            // Color code text
            {
              type: "text",
              value: colorCode,
              data: { processedByColorChip: true },
            },
          ],
        });

        lastIndex = match.index + colorCode.length;
      }

      // Add remaining text
      if (lastIndex < text.length) {
        newChildren.push({
          type: "text",
          value: text.slice(lastIndex),
          data: { processedByColorChip: true },
        });
      }

      // Replace original node with new children if we found any colors
      if (newChildren.length > 0) {
        parent.children.splice(index, 1, ...newChildren);
      }
    });
  };
}

export default plugin;
